/* app/research-analysis.jsx - shared analysis template (Research content engine).
   Config-driven AnalysisModule: list view + detail (5 run states) + capture system.
   Four lenses: Competitors / Partners / Industry analysis + Product Enrichment. */
import { api } from '../lib/api.js';
import { newRunId, pollRun } from '../lib/run.js';
(function () {
  const { useState, useRef, useEffect, useCallback } = React;
  function I(props) { return React.createElement(window.MlIcon, props); }
  const H = () => window.MlBiHelpers || {};

  // capture types: label + icon + destination
  const CAP = {
    keyword: ["Keyword", "target"], topic: ["Topic", "layers"], page: ["Page", "file-text"],
    faq: ["FAQ", "help-circle"], todo: ["To-do", "check-square"], blog: ["Blog page", "file-text"],
  };
  // "blog" is a page with a blog slant, so it shares the page bank and the page
  // capture type. Everything below counts in bank terms, not button terms.
  const bankType = (t) => (t === "blog" ? "page" : t);
  const CAP_ONE = { keyword: "keyword", topic: "topic", page: "page", faq: "FAQ", todo: "to-do" };
  const CAP_MANY = { keyword: "keywords", topic: "topics", page: "pages", faq: "FAQs", todo: "to-dos" };
  const nOf = (n, t) => n + " " + (n === 1 ? CAP_ONE[t] || t : CAP_MANY[t] || t);

  // Everything an analysis knew about a value (an intent, the cluster it grouped
  // it under, the note explaining it) used to be dropped at the button: only the
  // bare string travelled, and classification was then asked to re-derive from
  // that string what the analysis had already worked out.
  //
  // The capture routes answer with the facets that actually landed in a column
  // (an intent the schema's CHECK would reject is not one of them). This list is
  // the display order for that answer, never a substitute for it.
  const FACETS = ["cluster", "intent", "note", "detail", "answer", "priority", "page_type"];
  const has = (v) => v != null && String(v).trim() !== "";
  const cleanMeta = (meta) => {
    // A missing facet is left out entirely rather than sent as an empty value,
    // so a section with no facets captures bare instead of writing blanks.
    const out = {};
    Object.keys(meta || {}).forEach(k => { if (has(meta[k])) out[k] = meta[k]; });
    return out;
  };

  // A row banks one value by default. A row that stands for many (an industry
  // keyword cluster is one row and every keyword the analysis grouped under it)
  // declares `values`, each with the facets they share. A declared but empty
  // `values` means the analysis returned the group and no members, and nothing
  // is banked for it: naming the group as a keyword would invent a search term
  // the analysis never gave, which is also why the server refuses to.
  function rowValues(row) {
    if (Array.isArray(row.values)) return row.values.filter(v => v && has(v.value)).map(v => ({ value: String(v.value).trim(), meta: cleanMeta(v.meta) }));
    return has(row.title) ? [{ value: String(row.title).trim(), meta: cleanMeta(row.meta) }] : [];
  }
  // What a set of rows puts IN a bank: the tick, the untoggle and every count
  // read from here, so the screen can never disagree with itself.
  const bankValues = (rows, bt) => {
    const out = [];
    rows.forEach(r => { if ((r.cap || []).some(t => bankType(t) === bt)) out.push(...rowValues(r)); });
    return out;
  };
  // What is POSTED, which is not always the same list: a cluster row is sent as
  // one `keyword_cluster` item and the server fans it out to one keyword per
  // example, so that mapping lives in one place instead of two.
  const sendItems = (rows, bt) => {
    const out = [];
    rows.forEach(r => {
      if (!(r.cap || []).some(t => bankType(t) === bt)) return;
      if (r.bulkType) out.push({ capture_type: r.bulkType, value: r.title, meta: cleanMeta(r.meta) });
      else out.push(...rowValues(r));
    });
    return out;
  };
  const clustersOf = (values, only) => {
    const seen = {};
    values.forEach(v => { if (has(v.meta && v.meta.cluster) && (!only || only[v.value.toLowerCase()])) seen[v.meta.cluster] = 1; });
    return Object.keys(seen).length;
  };

  function CaptureBtn({ type, count, captured, busy, onToggle }) {
    const bt = bankType(type);
    const lbl = count > 1 ? nOf(count, bt) : CAP[type][0];
    return (
      <button className={"research-cap-btn" + (captured ? " is-captured" : "")} disabled={busy} onClick={onToggle}>
        {busy ? <span className="ml-spinner ml-spinner--sm" style={{ width: 12, height: 12 }} /> : <I name={captured ? "check" : "plus"} size={12} />}{lbl}
      </button>
    );
  }

  // a result row with capture buttons
  function ResultRow({ row, capValues, carried, busyKey, onCap }) {
    const vals = rowValues(row);
    return (
      <div className="research-row">
        <div className="research-row__head">
          <span className="research-row-title">{row.title}</span>
          {row.prio && <span className={"research-prio research-prio--" + row.prio}>{row.prio}</span>}
          {row.intent && <span className="research-tag research-tag--intent">{row.intent}</span>}
          {row.conf != null && <span className="research-tag research-tag--conf">{row.conf}%</span>}
        </div>
        {row.desc && <p className="research-row-desc">{row.desc}</p>}
        {row.cap && row.cap.length > 0 && vals.length === 0 && (
          <div className="research-cap-row"><span className="research-tag">{row.emptyNote || "nothing to capture"}</span></div>
        )}
        {row.cap && row.cap.length > 0 && vals.length > 0 && (
          <div className="research-cap-row">
            {row.cap.map(t => {
              const bt = bankType(t);
              const key = bt + "|" + row.id;
              const isCap = vals.every(v => (capValues[bt] || []).includes(v.value));
              return (
                <React.Fragment key={t}>
                  <CaptureBtn type={t} count={vals.length} captured={isCap} busy={busyKey === key} onToggle={() => onCap(bt, [row], key, true)} />
                  {/* What this row actually arrived with, shown only for the
                      captures made on this screen: capValues proves a value is
                      in the bank, not which facets it landed carrying, and a
                      row banked before this screen ran may well be bare. */}
                  {isCap && carried[key] && <span className="research-tag">with {carried[key]}</span>}
                </React.Fragment>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // Capture everything in a section at once: fifteen keywords was fifteen
  // clicks. One control per destination the section's rows offer, because a gap
  // can become a topic AND a page and those are two different banks.
  function SectionCaps({ section, capValues, busyKey, onCap }) {
    const rows = section.rows || [];
    const types = [];
    rows.forEach(r => (r.cap || []).forEach(t => { const bt = bankType(t); if (!types.includes(bt)) types.push(bt); }));
    const btns = types.map(bt => {
      const items = bankValues(rows, bt);
      const offering = rows.filter(r => (r.cap || []).some(t => bankType(t) === bt)).length;
      // With one row, or one value, the row's own button already captures the
      // whole section; a second control would be the same write relabelled.
      if (items.length < 2 || offering < 2) return null;
      const key = "all|" + bt + "|" + section.title;
      const done = items.every(it => (capValues[bt] || []).includes(it.value));
      const cl = clustersOf(items);
      // The industry clusters are the one section where the grouping IS the
      // value: say so on the button, because that grouping is what lets plan
      // assembly pull these keywords together later.
      const lbl = section.clustered && bt === "keyword" && cl > 0
        ? "All " + nOf(items.length, bt) + " in " + cl + (cl === 1 ? " cluster" : " clusters")
        : "All " + nOf(items.length, bt);
      return (
        <button key={bt} className={"research-cap-btn" + (done ? " is-captured" : "")} disabled={busyKey === key} onClick={() => onCap(bt, rows, key)}>
          {busyKey === key ? <span className="ml-spinner ml-spinner--sm" style={{ width: 12, height: 12 }} /> : <I name={done ? "check" : "plus"} size={12} />}{lbl}
        </button>
      );
    }).filter(Boolean);
    if (!btns.length) return null;
    return <div className="research-cap-row" style={{ marginTop: 0 }}>{btns}</div>;
  }

  /**
   * One section of an analysis, folded shut by default.
   *
   * An analysis now runs to a dozen sections and several hundred rows: every
   * article the target publishes, every term their URLs are built from, every
   * play they run. Printed open, that is a page nobody can see the shape of,
   * and the summary at the top gets lost above a kilometre of list. Shut, the
   * same page reads as a contents list: what this analysis found, in order,
   * with the size of each answer visible before you spend a scroll on it.
   *
   * Which is why the count is in the header rather than inside: "Article titles
   * 29" and "Article titles 313" are different findings, and a shut section
   * still has to tell you which one it is.
   *
   * `open` is deliberately NOT remembered between sections or visits. A fold
   * that restores itself sounds helpful and means the page looks different
   * every time you arrive, which is worse for a screen people compare across
   * competitors.
   */
  function Section({ title, right, count, children, defaultOpen = false }) {
    const [open, setOpen] = useState(!!defaultOpen);
    return (
      <div className={"bi-detail-section research-fold" + (open ? " research-fold--open" : "")}>
        <div className="bi-detail-section__h">
          {/* The whole header line toggles, not just the chevron: a 20px target
              on a 14px-tall row is a target people miss. The capture controls
              on the right sit OUTSIDE the button, or capturing a whole section
              would fold it shut in the same click. */}
          <button type="button" className="research-fold__btn" onClick={() => setOpen(v => !v)} aria-expanded={open}>
            <I name={open ? "chevron-down" : "chevron-right"} size={16} />
            <span>{title}</span>
            {count != null && <span className="research-fold__count">{count}</span>}
          </button>
          {open ? right : null}
        </div>
        {open && <div className="bi-detail-section__body">{children}</div>}
      </div>
    );
  }

  // ---- detail view ----
  function AnalysisDetail({ cfg, e, initialState, onBack, site, go }) {
    const { valBadge } = H();
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [phase, setPhase] = useState(e.analysed ? "loading" : (initialState || "never"));
    const [result, setResult] = useState(null);
    const [capValues, setCapValues] = useState({});
    const [promoted, setPromoted] = useState({});
    const [err, setErr] = useState(null);
    // Bumped after a capture actually landed. The nudge reads only when this
    // moves, so it costs nothing until the user has changed something.
    const [capTick, setCapTick] = useState(0);
    const [capErr, setCapErr] = useState(null);
    // The receipt for the last capture and, per row, the facets it travelled
    // with. Both are session state: a capture is only described by what this
    // screen actually sent.
    const [capNote, setCapNote] = useState(null);
    const [carried, setCarried] = useState({});
    const [busyKey, setBusyKey] = useState(null);

    const loadCaptures = useCallback(async () => {
      if (siteId == null) return;
      try { const r = await api(`/research/captures?site_id=${siteId}&entity_type=${cfg.entityType}&bi_entity_id=${e.id}`); setCapValues(r || {}); } catch { /* */ }
    }, [siteId, cfg, e]);

    useEffect(() => {
      let cancel = false;
      if (e.analysed && siteId != null) {
        api(`/research/analysis?site_id=${siteId}&entity_type=${cfg.entityType}&bi_entity_id=${e.id}`)
          .then(r => { if (cancel) return; if (r && r.found && r.result) { setResult(cfg.mapResult(r.result)); setPhase("result"); loadCaptures(); } else setPhase("never"); })
          .catch(() => { if (!cancel) setPhase("never"); });
      }
      return () => { cancel = true; };
    }, [e, siteId, cfg, loadCaptures]);

    // Analysis fetches a page then runs a long model call, which showed as an
    // undifferentiated spinner. The server reports each milestone it reaches.
    const [progress, setProgress] = useState(null);
    const run = async () => {
      if (siteId == null) { setErr("No active site."); setPhase("error"); return; }
      setPhase("analysing"); setErr(null); setProgress(null);
      const runId = newRunId();
      const stop = pollRun(runId, setProgress);
      try {
        const raw = await api(`/research/${cfg.endpoint}`, { method: "POST", body: JSON.stringify({ site_id: siteId, bi_entity_id: e.id, run_id: runId }) });
        setResult(cfg.mapResult(raw)); setPhase("result"); loadCaptures();
      } catch (ex) { setErr(ex.message || "Analysis failed"); setPhase("error"); }
      finally { stop(); setProgress(null); }
    };

    const capBase = { site_id: siteId, entity_type: cfg.entityType, bi_entity_id: e.id };

    // One row at a time, on /capture. Its duplicate check is scoped to THIS
    // analysis, which is exactly what the row's tick reads back, so a value
    // already banked from another entity still gets a row here and the toggle
    // stays truthful. /capture-bulk dedupes site-wide instead, which is the
    // right rule for filling a bank but would leave a row permanently unticked.
    // Every count below is what the server answered, never how many were sent.
    const captureEach = async (capType, items) => {
      const out = { added: 0, already: 0, failed: 0, elsewhere: 0, dupInRequest: 0, dropped: 0, noExamples: 0, facets: {}, unclassified: 0, ok: {} };
      for (const it of items) {
        const r = await api(`/research/capture`, { method: "POST", body: JSON.stringify({ ...capBase, capture_type: capType, value: it.value, meta: it.meta }) });
        if (r && r.already) { out.already += 1; continue; }
        out.added += 1;
        out.ok[it.value.toLowerCase()] = (r && r.facets) || [];
        ((r && r.facets) || []).forEach(f => { out.facets[f] = (out.facets[f] || 0) + 1; });
      }
      // The same rule the bulk route applies: capture writes no format, so a
      // keyword or topic is still in the classification queue however many
      // facets it arrived with.
      out.unclassified = (capType === "keyword" || capType === "topic") ? out.added : 0;
      return out;
    };

    // A whole section in one request, facets intact. The route reports what it
    // stored (inserted / skipped / failed, and which facets landed), and a run
    // where every write failed comes back as an error, so nothing here has to
    // guess. `keyword_cluster` items are fanned out server-side into one
    // keyword per example, each carrying the cluster it was grouped under.
    const captureBulk = async (capType, items) => {
      const r = await api(`/research/capture-bulk`, { method: "POST", body: JSON.stringify({ ...capBase, capture_type: capType, items }) }) || {};
      const list = Array.isArray(r.items) ? r.items : [];
      const num = (v) => (Number.isFinite(v) ? v : 0);
      const by  = (reason) => list.filter(x => x && x.reason === reason).length;
      const ok = {};
      list.forEach(x => { if (x && x.inserted) ok[String(x.value).toLowerCase()] = x.facets || []; });
      // `skipped` lumps together rows that were already there and rows that were
      // never attempted (empty, repeated inside the request, a cluster with no
      // examples). Calling all of them "already in the bank" would be a claim
      // about the bank that is not true, so they are separated by reason and the
      // total is only used when the server sent no per-row detail.
      const inBank = by("already_captured") + by("exists_in_bank");
      const named  = inBank + by("duplicate_in_request") + by("no_examples");
      return {
        added: num(r.inserted), failed: num(r.failed),
        already      : list.length ? inBank : num(r.skipped),
        elsewhere    : by("exists_in_bank"),
        dupInRequest : by("duplicate_in_request"),
        noExamples   : by("no_examples"),
        dropped      : list.length ? Math.max(0, num(r.skipped) - named) : 0,
        facets: (r.arrived_with && typeof r.arrived_with === "object") ? r.arrived_with : {},
        unclassified: num(r.still_unclassified),
        ok,
      };
    };

    // Says only what the server said it did. A skipped duplicate is never an
    // addition, a failed write is never silent, and the facets named are the
    // ones that actually landed in a column (`ok` is keyed by the values the
    // server confirmed it stored).
    const capReport = (capType, values, res) => {
      const cl = clustersOf(values, res.ok);
      const tail = cl > 0 ? " across " + cl + (cl === 1 ? " cluster" : " clusters") : "";
      const f = Object.keys(res.facets || {}).filter(k => FACETS.includes(k) && res.facets[k] > 0);
      const carrying = f.length
        ? ", carrying " + f.map(k => k + " (" + res.facets[k] + ")").join(", ")
        : ", carrying no facets: the analysis returned none for these";
      const out = [res.added > 0 ? "Added " + nOf(res.added, capType) + tail + carrying + "." : "Added nothing new."];
      if (res.already > 0) {
        out.push(res.already + (res.already === 1 ? " was" : " were") + " already in the bank"
          + (res.elsewhere > 0 ? " (" + res.elsewhere + " from another analysis, so not added again here)" : "") + ".");
      }
      if (res.failed > 0) out.push(res.failed + (res.failed === 1 ? " row" : " rows") + " failed to write and " + (res.failed === 1 ? "was" : "were") + " not stored.");
      if (res.dupInRequest > 0) out.push(res.dupInRequest + (res.dupInRequest === 1 ? " value repeated" : " values repeated") + " inside this section and " + (res.dupInRequest === 1 ? "was" : "were") + " banked once.");
      if (res.noExamples > 0) out.push(res.noExamples + (res.noExamples === 1 ? " cluster had" : " clusters had") + " no examples, so nothing was captured for " + (res.noExamples === 1 ? "it" : "them") + ".");
      if (res.dropped > 0) out.push(res.dropped + (res.dropped === 1 ? " row was" : " rows were") + " left out as empty or unsupported.");
      // Facets do not make a row triaged: capture writes no format and status
      // stays raw, so these are still in the classification queue.
      if (res.added > 0 && res.unclassified > 0) out.push("All arrive at status raw: " + res.unclassified + " still queued for classification.");
      return out.join(" ");
    };

    // rows is one row for the per-item toggle, the whole section for a bulk
    // capture. Both go through here so the toggle keeps its untoggle and the
    // bulk capture inherits the same honest error surface. Only the per-row
    // button untoggles: a section control that deleted a dozen banked rows on a
    // second click would be a trap, so there it re-checks and reports instead.
    const onCap = async (capType, rows, key, canUndo) => {
      if (siteId == null) return;
      const values = bankValues(rows, capType);
      if (!values.length) return;
      const captured = canUndo && values.every(v => (capValues[capType] || []).includes(v.value));
      let changed = false;
      setCapErr(null); setCapNote(null); setBusyKey(key);
      try {
        if (captured) {
          for (const v of values) {
            await api(`/research/capture`, { method: "DELETE", body: JSON.stringify({ ...capBase, capture_type: capType, value: v.value }) });
          }
          setCarried(c => { const n = { ...c }; rows.forEach(r => { delete n[capType + "|" + r.id]; }); return n; });
          setCapNote("Removed " + nOf(values.length, capType) + " from the bank.");
          changed = true;
        } else {
          const res = canUndo ? await captureEach(capType, values) : await captureBulk(capType, sendItems(rows, capType));
          changed = res.added > 0;
          // Only rows the server confirmed it stored get a facet badge: a row
          // that was skipped as a duplicate was written by an earlier call and
          // this one learned nothing about what it carries.
          setCarried(c => {
            const n = { ...c };
            rows.forEach(r => {
              const f = {};
              rowValues(r).forEach(v => (res.ok[v.value.toLowerCase()] || []).forEach(k => { f[k] = 1; }));
              const names = FACETS.filter(k => f[k]);
              if (names.length) n[capType + "|" + r.id] = names.join(", ");
            });
            return n;
          });
          setCapNote(capReport(capType, values, res));
        }
        loadCaptures();
        // The nudge reads only when the bank actually moved. A section where
        // every value was already there wrote nothing, so there is nothing new
        // for it to say.
        if (changed) setCapTick(t => t + 1);
      } catch (ex) {
        // The row's own tick state comes from loadCaptures, so a swallowed
        // failure left the item looking captured on the next repaint when it
        // was not written at all.
        setCapErr(ex.message || "That capture did not save.");
      } finally { setBusyKey(null); }
    };

    const promote = async (d) => {
      if (siteId == null) return;
      setPromoted(p => ({ ...p, [d.id]: "adding" }));
      try {
        await api(`/bi/entities`, { method: "POST", body: JSON.stringify({ site_id: siteId, entity_type: cfg.entityType, name: d.name, url: d.url || null, data: { relationships_to_me: d.rels || [] }, source: "ai_suggested", validation_status: "pending" }) });
        setPromoted(p => ({ ...p, [d.id]: "done" }));
      } catch { setPromoted(p => ({ ...p, [d.id]: undefined })); }
    };

    return (
      <div className="bi-detail" style={{ maxWidth: 820 }}>
        <button className="bi-detail__back" onClick={onBack}><I name="chevron-left" size={16} />Back to {cfg.plural}</button>
        <div className="bi-detail-head">
          <div className="bi-detail__name">{e.name}</div>
          {phase === "result" && <button className="bi-btn" onClick={run}><I name="refresh" size={16} />{cfg.reverb}</button>}
        </div>

        {phase === "result" && <div className="research-analyzed-banner"><I name="check-circle" size={14} />Last {cfg.verbed} <b>{e.analysed || "just now"}</b></div>}

        {capErr && <div className="ds-strip ds-strip--error" style={{ marginBottom: 12 }}><I name="alert-triangle" size={14} /><span>{capErr}</span><button className="bi-btn bi-btn--sm" onClick={() => setCapErr(null)}>Dismiss</button></div>}
        {/* The receipt for the last capture. A failure clears it and shows the
            error strip instead, so an error can never read as a quiet success. */}
        {capNote && !capErr && <div className="ds-strip ds-strip--quiet" style={{ marginBottom: 12 }}><I name="check-circle" size={14} /><span style={{ flex: 1 }}>{capNote}</span><button className="bi-btn bi-btn--sm" onClick={() => setCapNote(null)}>Dismiss</button></div>}
        {/* What the capture just made possible, on the screen it happened on.
            Reads only when capTick moves, so browsing an analysis costs nothing. */}
        {window.MlDiscovery && <window.MlDiscovery.Nudge siteId={siteId} trigger={capTick} go={go} />}

        {phase === "loading" && <div className="research-runstate"><span className="ml-spinner ml-spinner--lg" style={{ marginBottom: 16 }} /><div className="research-runstate__title">Opening {e.name}…</div></div>}

        {phase === "analysing" && (
          <div className="research-runstate">
            <span className="research-runstate__icon"><span className="ml-spinner ml-spinner--lg ml-spinner--ai" /></span>
            <div className="research-runstate__title">{cfg.verbing} {e.name}…</div>
            {progress ? (
              <>
                <div className="ml-progress-row" style={{ maxWidth: 340, margin: "14px auto 8px" }}>
                  <div className="ml-progress"><div className="ml-progress__bar ml-progress__bar--ai" style={{ width: progress.pct + "%" }} /></div>
                  <span className="ml-progress-row__pct">{progress.pct}%</span>
                </div>
                <p className="research-runstate__desc">
                  Step {Math.max(1, progress.step)} of {progress.steps} · {progress.label}{progress.note ? ` · ${progress.note}` : ""}
                </p>
              </>
            ) : (
              <p className="research-runstate__desc">Fetching the site and running content &amp; SEO analysis. This can take a few seconds.</p>
            )}
          </div>
        )}

        {phase === "error" && (
          <div className="research-runstate">
            <span className="research-runstate__icon research-runstate__icon--danger"><I name="alert-circle" size={28} /></span>
            <div className="research-runstate__title">Couldn't {cfg.verb} {e.name}</div>
            {/* The server's own sentence: a rejected AI key, an unreachable
                site and a refused fetch need different fixes, and showing one
                hardcoded guess ("the site didn't respond") sent users to
                check URLs that answered fine. */}
            <p className="research-runstate__desc">{err || "The request failed. Try again."}</p>
            <div className="research-runstate__actions"><button className="bi-btn" onClick={onBack}>Back</button><button className="bi-btn bi-btn--primary" onClick={run}><I name="refresh" size={16} />Retry</button></div>
          </div>
        )}

        {phase === "never" && (
          <div className="research-runstate">
            <span className="research-runstate__icon"><I name={cfg.icon} size={28} /></span>
            <div className="research-runstate__title">Not {cfg.verbed} yet</div>
            <p className="research-runstate__desc">{cfg.neverDesc}</p>
            <div className="research-runstate__actions"><button className="bi-btn bi-btn--ai" onClick={run}><I name="sparkles" size={16} />Run {cfg.noun}</button></div>
            <div className="research-cost-note"><I name="alert-triangle" size={14} />Makes a Claude call · uses tokens</div>
          </div>
        )}

        {phase === "result" && result && (
          <>
            <p className="research-summary">{result.summary}</p>
            {result.sections.map((s, i) => (
              // Shut by default, with two exceptions that are not findings but
              // warnings about the findings: an answer that came back
              // incomplete, and what the site already covers. Burying either
              // behind a fold would be hiding the caveat and showing the claim.
              <Section key={i} title={s.title}
                count={s.kind === "rows" ? s.rows.length : s.kind === "chips" ? s.chips.length : null}
                defaultOpen={s.kind === "prose" && /incomplete|already covers/i.test(s.title)}
                right={s.kind === "rows" ? <SectionCaps section={s} capValues={capValues} busyKey={busyKey} onCap={onCap} /> : null}>
                {s.kind === "prose" && <p className="research-prose">{s.text}</p>}
                {s.kind === "chips" && <div className="research-chips">{s.chips.map((c, j) => <span className="research-chip" key={j}>{c.b && <b>{c.b}</b>}{c.t}</span>)}</div>}
                {s.kind === "rows" && <div className="research-rows">{s.rows.map(r => <ResultRow key={r.id} row={r} capValues={capValues} carried={carried} busyKey={busyKey} onCap={onCap} />)}</div>}
              </Section>
            ))}
            {cfg.discovers && result.discovered && (
              <Section title="Discovered entities">
                <div className="research-discovered">
                  {result.discovered.map(d => (
                    <div className="research-disc-card" key={d.id}>
                      <div className="research-disc-card__main">
                        <div className="research-disc-card__name">{d.name}<span className="research-tag research-tag--conf">{d.conf}%</span></div>
                        <div className="research-disc-card__sum">{d.sum}</div>
                      </div>
                      <button className={"research-promote" + (promoted[d.id] === "done" ? " is-promoted" : promoted[d.id] === "adding" ? " is-adding" : "")} onClick={() => !promoted[d.id] && promote(d)}>
                        {promoted[d.id] === "done" ? <><I name="check" size={13} />Added to BI</> : promoted[d.id] === "adding" ? <><span className="ml-spinner ml-spinner--sm" style={{ width: 12, height: 12 }} />Adding…</> : <><I name="plus" size={13} />Promote to BI</>}
                      </button>
                    </div>
                  ))}
                </div>
                <div className="research-promote-note">Promoted entities enter BI as <b>pending validation</b> - review them in BI before they're trusted.</div>
              </Section>
            )}
          </>
        )}
      </div>
    );
  }

  // ---- list view ----
  // `detailId` is which entity's analysis is open, and it comes from the URL
  // (see the location convention in main.jsx): clicking "View analysis" on one
  // competitor used to be invisible to the address bar, so a refresh dropped
  // the user back on the list with nothing naming what they had been reading.
  // `onDetail(id|null)` reports every open and close back so the hash follows.
  function AnalysisModule({ cfg, listState, site, go, detailId, onDetail }) {
    const { valBadge } = H();
    // Each analysis lens analyses a BI entity type, and "Go to BI" jumps to that
    // module so the user can add/grow entities, then come back to analyse them.
    const BI_NAV = { Competitors: "bi-competitors", Partners: "bi-partners", Industry: "bi-industry", Products: "products" };
    const biNav = BI_NAV[cfg.biLabel] || "business-profile";
    const siteId = site && (site.site_id != null ? site.site_id : site.id);
    const [stat, setStat] = useState("all");
    const [selectMode, setSelectMode] = useState(false);
    const [sel, setSel] = useState([]);
    // Which analysis is open is the host's state now, not this module's, because
    // it is really the URL's. Kept behind the same detail/setDetail pair every
    // call site below already used, so nothing else in here had to change: the
    // local state is only the fallback for a host that does not own the
    // location (the component gallery mounts these modules bare).
    const [localDetail, setLocalDetail] = useState(null);
    const detail = onDetail ? (detailId == null ? null : { id: detailId }) : localDetail;
    const setDetail = onDetail ? ((d) => onDetail(d == null ? null : d.id)) : setLocalDetail;
    const [confirm, setConfirm] = useState(null);
    const [running, setRunning] = useState(0);
    const [batchErr, setBatchErr] = useState(null);
    const [viewMode, setViewMode] = useState("grid");
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const toggle = (id) => setSel(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);

    const load = useCallback(async () => {
      if (siteId == null) { setData([]); setLoading(false); return; }
      setLoading(true);
      try {
        const [biRows, analyses] = await Promise.all([
          api(`/bi/entities?site_id=${siteId}&entity_type=${cfg.entityType}&owner_flag=0`).catch(() => []),
          api(`/research/analyses?site_id=${siteId}&entity_type=${cfg.entityType}`).catch(() => []),
        ]);
        const seen = {}; (Array.isArray(analyses) ? analyses : []).forEach(a => { seen[a.bi_entity_id] = a.analyzed_at; });
        setData((Array.isArray(biRows) ? biRows : []).filter(r => r.validation_status !== "rejected").map(r => {
          const at = seen[r.id]; return { ...cfg.mapEntity(r), analysed: at ? String(at).slice(0, 10) : null };
        }));
      } catch { setData([]); }
      finally { setLoading(false); }
    }, [siteId, cfg]);
    useEffect(() => { load(); }, [load]);

    const rows = data.filter(e => stat === "all" ? true : cfg.statFilter(stat, e));
    const stats = cfg.stats.map(([id, label, color]) => [id, label, color, data.filter(e => cfg.statFilter(id, e)).length]);

    // A link straight to one analysis arrives before the list it belongs to, so
    // the entity it names is legitimately absent for a moment. Three answers,
    // never one: the entity, the wait, or a line saying it is not here. The
    // blank page this used to render for all three was indistinguishable from a
    // broken screen, and for an id that no longer exists (deleted, archived as
    // rejected, or belonging to another site) it never stopped being blank.
    const detailEntity = detail && data.find(x => x.id === detail.id);
    if (detailEntity) {
      return <div className="bi-page"><AnalysisDetail cfg={cfg} e={detailEntity} initialState={detail.state} onBack={() => { setDetail(null); load(); }} site={site} go={go} /></div>;
    }
    if (detail && loading) {
      return <div className="bi-page"><div className="research-runstate"><span className="ml-spinner ml-spinner--lg" style={{ marginBottom: 16 }} /><div className="research-runstate__title">Opening the {cfg.noun}…</div></div></div>;
    }
    // Falls through to the list below, with the reason on top of it.
    const gone = !!detail;

    // Ran a 700ms counter and cleared the selection: the confirm dialog warned
    // that it "will use tokens", the count ticked up, and no analysis was ever
    // requested. Each selected entity is now analysed for real, one at a time
    // (they are long calls; firing them together would hit the rate limit), and
    // a failure stops the batch instead of being counted as done.
    const runBatch = async () => {
      setConfirm(null);
      const ids = sel.slice();
      if (!ids.length || siteId == null) return;
      setBatchErr(null);
      for (let i = 0; i < ids.length; i++) {
        setRunning(i + 1);
        try {
          await api(`/research/${cfg.endpoint}`, { method: "POST", body: JSON.stringify({ site_id: siteId, bi_entity_id: ids[i] }) });
        } catch (e) {
          const name = (data.find(x => x.id === ids[i]) || {}).name || `item ${i + 1}`;
          setBatchErr(`Stopped at ${name}: ${e.message || "analysis failed"}. ${i} of ${ids.length} completed.`);
          break;
        }
      }
      setRunning(0); setSelectMode(false); setSel([]);
      await load();
    };

    const freshness = (e) => e.analysed
      ? <span className="research-when"><I name="check-circle" size={13} />Last {cfg.verbed} <b>{e.analysed}</b>{e.caps && <> · <span className="research-when__caps">{e.caps}</span></>}</span>
      : <span className="research-never"><I name="circle" size={13} />Not {cfg.verbed} yet</span>;

    const renderGridCard = (e) => {
      const card = (
        <div className="bi-card" tabIndex={0} onClick={() => setDetail({ id: e.id })}>
          <div className="bi-card-head">
            <div className="bi-card__name">{e.offer && <I name="star" size={14} className="bi-ours-star" style={{ fill: "currentColor", color: "var(--indigo-600)" }} />}{e.name}</div>
            <div className="bi-card-badges">{cfg.cardBadges(e)}</div>
          </div>
          <div className="bi-card__meta">{e.meta.map(([l, v]) => <div className="bi-meta-row" key={l}><span className="bi-meta-row__l">{l}</span><span className="bi-meta-row__v">{v}</span></div>)}</div>
          <div className="bi-card-footer-split">
            {freshness(e)}
            <button className="bi-btn bi-btn--sm bi-btn--primary" onClick={(ev) => { ev.stopPropagation(); setDetail({ id: e.id }); }}>{e.analysed ? "View analysis" : "Open"}<I name="arrow-right" size={14} /></button>
          </div>
        </div>
      );
      if (!selectMode) return <React.Fragment key={e.id}>{card}</React.Fragment>;
      return <div key={e.id} className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(e.id) ? " bi-select-wrap--selected" : "")}><label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={sel.includes(e.id)} onChange={() => toggle(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>{card}</div>;
    };

    const renderRow = (e) => {
      const row = (
        <div className="bi-row" tabIndex={0} onClick={() => setDetail({ id: e.id })}>
          <div className="bi-row__main">
            <span className="bi-row__name">{e.offer && <I name="star" size={13} className="bi-ours-star" />}{e.name}</span>
            <span className="bi-row__url">{e.meta[0] && e.meta[0][1]}</span>
          </div>
          <div className="bi-row__badges">{cfg.cardBadges(e)}</div>
          <div className="bi-row__meta" style={{ flexBasis: 240, flexGrow: 0 }}>{freshness(e)}</div>
          <div className="bi-row__actions"><button className="bi-btn bi-btn--sm bi-btn--primary" onClick={(ev) => { ev.stopPropagation(); setDetail({ id: e.id }); }}>{e.analysed ? "View" : "Open"}<I name="arrow-right" size={14} /></button></div>
        </div>
      );
      if (!selectMode) return <React.Fragment key={e.id}>{row}</React.Fragment>;
      return <div key={e.id} className={"bi-select-wrap bi-select-wrap--on" + (sel.includes(e.id) ? " bi-select-wrap--selected" : "")}><label className="hds-check bi-select-wrap__check" onClick={ev => ev.stopPropagation()}><input type="checkbox" checked={sel.includes(e.id)} onChange={() => toggle(e.id)} /><span className="hds-check__box"><I name="check" size={12} /></span></label>{row}</div>;
    };

    return (
      <div className="bi-page">
        <div className="bi-header">
          <div className="bi-header__titles">
            <h1 className="bi-title"><span className="bi-title__icon"><I name={cfg.icon} size={20} /></span>{cfg.title}</h1>
          </div>
          <div className="bi-header-actions">
            <div className="bi-viewtoggle" role="tablist" aria-label="View mode">
              <button className={viewMode === "grid" ? "on" : ""} title="Grid view" aria-pressed={viewMode === "grid"} onClick={() => setViewMode("grid")}><I name="grid" size={16} /></button>
              <button className={viewMode === "list" ? "on" : ""} title="List view" aria-pressed={viewMode === "list"} onClick={() => setViewMode("list")}><I name="list" size={16} /></button>
            </div>
            <button className="bi-btn" onClick={() => { setSelectMode(s => !s); setSel([]); }}>{selectMode ? <><I name="x" size={16} />Cancel</> : <><I name="check-square" size={16} />Select</>}</button>
            <button className="bi-btn" onClick={load}><I name="refresh" size={16} />Refresh</button>
          </div>
        </div>
        <p className="research-intro">{cfg.intro}</p>
        {gone && <div className="bi-subempty">That link points to an entry that is not in this list. It may have been deleted or archived, or it belongs to another site.</div>}

        {(!loading && data.length === 0) ? (
          <div className="bi-empty-state">
            <span className="bi-empty-state__icon"><I name={cfg.icon} size={30} /></span>
            <div className="bi-empty-state__title">No {cfg.plural} to analyse</div>
            <p className="bi-empty-state__desc">{cfg.emptyDesc}</p>
            <div className="bi-empty-state__actions"><button className="bi-btn bi-btn--primary" onClick={() => go && go(biNav)}><I name="arrow-right" size={16} />Go to BI · {cfg.biLabel}</button></div>
          </div>
        ) : (
          <>
            <div className="bi-stat-cards" style={{ gridTemplateColumns: "repeat(" + cfg.stats.length + ", 1fr)", maxWidth: 560 }}>
              {stats.map(([id, label, color, val]) => (
                <button key={id} className={"bi-stat-card bi-stat-card--clickable" + (stat === id ? " bi-stat-card--active" : "")} onClick={() => setStat(stat === id ? "all" : id)}>
                  <div className="bi-stat-card__top"><span className="bi-stat-card__dot" style={{ background: color }} /><span className="bi-stat-card__label">{label}</span></div>
                  <span className="bi-stat-card__value">{val}</span>
                </button>
              ))}
            </div>

            {selectMode && (
              <div className="bi-bulk-bar">
                {running > 0 ? (
                  <span className="research-progress-read"><span className="ml-spinner ml-spinner--sm ml-spinner--ai" />Running {running}/{sel.length}…</span>
                ) : (
                  <>
                    <span className="bi-bulk-bar__count">{sel.length} selected</span>
                    <button className="bi-btn bi-btn--sm" onClick={() => setSel(rows.map(e => e.id))}><I name="check-square" size={14} />Select all ({rows.length})</button>
                    <button className="bi-btn bi-btn--sm" onClick={() => setSel([])}>Clear</button>
                    <span className="bi-bulk-bar__sep" />
                    <button className="bi-btn bi-btn--sm bi-btn--ai" disabled={!sel.length} onClick={() => setConfirm("analyse")}><I name="sparkles" size={14} />{cfg.batchVerb}</button>
                  </>
                )}
              </div>
            )}

            {viewMode === "list"
              ? <div className="bi-list--rows">{rows.map(renderRow)}</div>
              : <div className="bi-list">{rows.map(renderGridCard)}</div>}
          </>
        )}

        {batchErr && <div className="bi-hint" style={{ color: "var(--danger)", marginBottom: 10 }}>{batchErr}</div>}
        {confirm && window.MlBiModals && <window.MlBiModals.ConfirmDialog title={cfg.batchVerb + " " + (sel.length || 0) + " items?"} body={"This runs " + (sel.length || 0) + " AI " + cfg.noun + "s and will use tokens."} confirmLabel={cfg.batchVerb} onClose={() => setConfirm(null)} onConfirm={runBatch} />}
      </div>
    );
  }

  window.MlResearchAnalysis = { AnalysisModule, AnalysisDetail };
})();
